import {AddressDto, DayOfWeekType, LookupValueDto, OpeningHoursDto} from '@/generated/openapi/api'
import {Validation, ValidationRuleWithoutParams} from '@vuelidate/core'
import {required, helpers, maxLength, minLength, url} from '@vuelidate/validators'
import {allWeekdays} from './apiUtils'

function requiredInteger(value: number): boolean {
    if (typeof value === 'number' && Number.isInteger(value)) {
        return true
    }
    return false
}

function privacyNoticeRequired(val: any) {
    return val === true
}

function lookupValueNecessary(lookupValueIdsSelected: string[], atLeastOneOf: LookupValueDto[]): boolean {
    if (atLeastOneOf === undefined || atLeastOneOf.length === 0) {
        return true
    }
    if (lookupValueIdsSelected === undefined || lookupValueIdsSelected.length === 0) {
        return false
    }
    const tmpArray = atLeastOneOf.map(lvdto => lvdto.id)
    for (const lvId of lookupValueIdsSelected) {
        if (tmpArray.includes(lvId)) {
            return true
        }
    }
    return false
}

function isValidHour(val: number | undefined) {
    return typeof val === 'number' && Number.isInteger(val) && val >= 0 && val <= 23
}

function isValidMinute(val: number | undefined) {
    return typeof val === 'number' && Number.isInteger(val) && val >= 0 && val <= 59
}

function requiredTime(value: string) {
    if (typeof value === 'string' && isValidHour(parseInt(value.split(':')[0])) && isValidMinute(parseInt(value.split(':')[1]))) {
        return true
    }
    return false
}

const isValidTime = helpers.withMessage(
    'Time must be in HH:mm format',
    (value: string) => {
        const match = value.match(/(\d{2}:\d{2})/);
        if (!match) {
            console.log("No valid time found in the input:", value);
            return false;
        }
        const extractedTime = match[1];
        return /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(extractedTime);
    }
);



function isAfterOpeningTime(value: string | undefined, vm: Validation): boolean {
    const startTime = vm.openingTime || ''
    const endTime = value || ''
    return new Date('2020-01-01 ' + endTime) > new Date('2020-01-01 ' + startTime)
}

/**
 * The returned value gives the difference between the two times in Minutes.<br>
 * Only works with time format "hh:mm"
 *
 * @param timeA e.g. "10:00"
 * @param timeB e.g. "12:00" -
 * @returns 0 if same; >0 if timeA > timeB; <0 if timeA < timeB
 */
function clockTimeComparator(timeA: string | undefined, timeB: string | undefined): number {
    if (!timeA) timeA = "00:00"
    if (!timeB) timeB = "00:00"
    // Make comparable numerical interpretation
    const valA = (parseInt(timeA.split(':')[0]) * 1000) + (parseInt(timeA.split(':')[1]))
    const valB = (parseInt(timeB.split(':')[0]) * 1000) + (parseInt(timeB.split(':')[1]))
    return (valA - valB) / 60 * 100
}

/**
 * Compares two days and returns the difference. If no day is given it's considered "Sunday"
 * @param dayA
 * @param dayB
 * @returns
 */
function dayOfWeekComparator(dayA: DayOfWeekType | undefined, dayB: DayOfWeekType | undefined): number {
    if (!dayA) dayA = DayOfWeekType.Sunday
    if (!dayB) dayB = DayOfWeekType.Sunday
    let idxDayA = 6
    let idxDayB = 6
    let idx = 0
    allWeekdays().forEach((elem) => {
        if (dayA === elem) idxDayA = idx
        if (dayB === elem) idxDayB = idx
        idx++
    });
    return idxDayA - idxDayB
}

function openingHoursComparator(a: OpeningHoursDto, b: OpeningHoursDto): number {
    const dayA = a.dayOfWeek
    const dayB = b.dayOfWeek
    if (dayA === dayB) {
        let timeDiff = clockTimeComparator(a.openingTime, b.openingTime)
        if (timeDiff !== 0) {
            return timeDiff
        }
        timeDiff = clockTimeComparator(a.closingTime, a.closingTime)
        return timeDiff
    }
    // Days are different. Compare the days.
    return dayOfWeekComparator(dayA, dayB)
}


function addressDtoValidations() {
    return {
        zipCode: {
            required,
            minLength: minLength(4),
            maxLength: maxLength(4),
            zip
        },
        city: {
            required,
            maxLength: maxLength(150)
        },
        streetName: {
            required,
            maxLength: maxLength(150)
        },
        houseNumber: {
            required,
            maxLength: maxLength(50)
        },
        addressSupplement: {
            maxLength: maxLength(150)
        },
        region: {
            required,
            maxLength: maxLength(50)
        },
        areaCode: {
            required,
            maxLength: maxLength(10)
        }
    }
}

const isValidEmail = (email: string) => {
    const regex = /^[\w-]+(\.[\w-]+)*@([\w-]+\.)+[a-zA-Z]{2,7}$/;
    return regex.test(email);
};

const isValidPhone = (phone: string) => {
    const regex = /^\+(?:[0-9]{1,3} )[1-9][0-9]{0,3} (?:(?:[0-9]{3,8} ){1,3}[0-9]{1,8}|[0-9]{3,8})$/;
    return regex.test(phone);
};

const isValidRegistryNumber = (registryNumber: string) => {
    const regex = /^[a-zA-Z0-9\-\\/,.:_#]+$/;
    return regex.test(registryNumber);
}

const isTelephoneString = (value?: string) => {
  if (!value) {
    return true
  }
  return /^[0-9+]*$/gi.test(value);
}

const isAddressValid = (addr?: AddressDto): boolean => {
  return addr !== undefined &&
    addr.city !== null && addr.city !== undefined &&
    addr.streetName !== null && addr.streetName !== undefined &&
    addr.zipCode !== null && addr.zipCode !== undefined &&
    addr.houseNumber !== null && addr.houseNumber !== undefined &&
    addr.areaCode !== null && addr.areaCode !== undefined &&
    addr.region !== null && addr.region !== undefined
}

const zip = helpers.regex(/^\d{4}$/) as ValidationRuleWithoutParams<any>

/**
 * International telephone number validator
 */

const tel = helpers.regex(/^\+(?:[0-9]{1,3} )[1-9][0-9]{0,3} (?:(?:[0-9]{3,8} ){1,3}[0-9]{1,8}|[0-9]{3,8})$/) as ValidationRuleWithoutParams<any>
// REGEX tel erklärt         ^[int.Vorwahl]  ^[vorwOhne0]    (  ^[dreierGrupp]  ^[zahl]   |^[ein block nur zahlen])

// YYYY-MM-DD
const oDate = helpers.regex(/^\d{4}-\d{2}-\d{2}$/);


export {
    requiredInteger,
    addressDtoValidations,
    privacyNoticeRequired,
    lookupValueNecessary,
    requiredTime,
    isAfterOpeningTime,
    isValidRegistryNumber,
    clockTimeComparator,
    dayOfWeekComparator,
    openingHoursComparator,
    tel,
    zip,
    oDate,
    isValidEmail,
    isValidPhone,
    isValidTime,
    isAddressValid,
    isTelephoneString
}
